/**
 * Israeli bank account selector – area-based grouping from formConfig.
 * Wires israeliBankSelector, israeliBankBranch, israeliBankBranchNumber, israeliBankNumber.
 */

import { FormConfig } from '../core/form_config.js';
import {
  loadBranchMap,
  getBankNamesFromXml,
  getBranchesForBank,
  getBankCodeForBankName,
  getBranchNameFromMap
} from './bank_helpers.js';

const INPUT_FN = {
  SELECTOR: 'israeliBankSelector',
  BRANCH: 'israeliBankBranch',
  BRANCH_NUMBER: 'israeliBankBranchNumber',
  BANK_NUMBER: 'israeliBankNumber'
};

/**
 * Build bank groups from formConfig: areas that contain all 4 Israeli bank field types.
 * @param {Object} formConfig - Form configuration
 * @returns {Array<{ areaId: string, bankSelectorId: string, branchId: string, branchNumberId: string, bankNumberId: string }>}
 */
export function buildBankGroupsFromConfig(formConfig) {
  if (!formConfig?.areas || !formConfig?.fields) return [];
  const groups = [];
  formConfig.areas.forEach(area => {
    const fieldIds = area.fields;
    if (!Array.isArray(fieldIds) || fieldIds.length === 0) return;
    let bankSelectorId = null;
    let branchId = null;
    let branchNumberId = null;
    let bankNumberId = null;
    fieldIds.forEach(fieldId => {
      const field = FormConfig.getFieldById(formConfig, fieldId);
      if (!field) return;
      const fn = field.inputFunction || field.input_function;
      if (fn === INPUT_FN.SELECTOR) bankSelectorId = field.id;
      else if (fn === INPUT_FN.BRANCH) branchId = field.id;
      else if (fn === INPUT_FN.BRANCH_NUMBER) branchNumberId = field.id;
      else if (fn === INPUT_FN.BANK_NUMBER) bankNumberId = field.id;
    });
    if (bankSelectorId && branchId && branchNumberId && bankNumberId) {
      groups.push({
        areaId: area.id,
        bankSelectorId,
        branchId,
        branchNumberId,
        bankNumberId
      });
    }
  });
  return groups;
}

/**
 * Get bank selector field ID for the area that contains the given branch field (for populateFormData).
 * @param {Object} formConfig - Form configuration
 * @param {string} branchFieldId - Field ID of israeliBankBranch or the field name (without [])
 * @returns {string|null}
 */
export function getBankSelectorIdForBranchField(formConfig, branchFieldId) {
  const groups = buildBankGroupsFromConfig(formConfig);
  const baseId = branchFieldId.replace(/\[\]$/, '');
  const group = groups.find(g => g.branchId === baseId);
  return group ? group.bankSelectorId : null;
}

/**
 * Find the 4 elements within a row by data-input-function.
 * @param {Element} row
 * @returns {{ bankSelect: HTMLSelectElement|null, branchInput: HTMLInputElement|null, branchNumberInput: HTMLInputElement|null, bankNumberInput: HTMLInputElement|null }}
 */
function findElementsInRow(row) {
  const sel = row.querySelector(`select[data-input-function="${INPUT_FN.SELECTOR}"]`);
  const branch = row.querySelector(`input[data-input-function="${INPUT_FN.BRANCH}"]`);
  const branchNum = row.querySelector(`input[data-input-function="${INPUT_FN.BRANCH_NUMBER}"]`);
  const bankNum = row.querySelector(`input[data-input-function="${INPUT_FN.BANK_NUMBER}"]`);
  return {
    bankSelect: sel,
    branchInput: branch,
    branchNumberInput: branchNum,
    bankNumberInput: bankNum
  };
}

/**
 * Get bank group elements for the row/area containing the given element.
 * @param {Element} el - Any element (e.g. event target)
 * @param {Array} groups - From buildBankGroupsFromConfig
 * @returns {{ bankSelect: HTMLSelectElement|null, branchInput: HTMLInputElement|null, branchNumberInput: HTMLInputElement|null, bankNumberInput: HTMLInputElement|null }|null}
 */
export function getBankGroupForElement(el, groups) {
  if (!el || !groups?.length) return null;
  const row = el.closest('.row');
  const area = el.closest('.area');
  if (!row || !area) return null;
  const areaId = area.getAttribute('data-id');
  const group = groups.find(g => g.areaId === areaId);
  if (!group) return null;
  const found = findElementsInRow(row);
  if (!found.bankSelect) return null;
  return { ...found, group };
}

/**
 * Get all row-level groups (each row in each bank area) with their DOM elements and group config.
 * @param {Element} container - Form container
 * @param {Array} groups - From buildBankGroupsFromConfig
 * @returns {Array<{ row: Element, group: Object, bankSelect: HTMLSelectElement|null, branchInput: HTMLInputElement|null, branchNumberInput: HTMLInputElement|null, bankNumberInput: HTMLInputElement|null }>}
 */
export function getAllRowGroups(container, groups) {
  if (!container || !groups?.length) return [];
  const result = [];
  groups.forEach(group => {
    const areaEl = container.querySelector(`.area[data-id="${group.areaId}"]`);
    if (!areaEl) return;
    const rows = areaEl.querySelectorAll('.row');
    rows.forEach(row => {
      const els = findElementsInRow(row);
      if (els.bankSelect) {
        result.push({ row, group, ...els });
      }
    });
  });
  return result;
}

/**
 * Get current value from bank select (including NiceSelect wrapper).
 * @param {HTMLSelectElement} bankSelectEl
 * @returns {string}
 */
function getBankSelectValue(bankSelectEl) {
  let v = (bankSelectEl.value || '').trim();
  if (!v && bankSelectEl.parentNode) {
    const ns = bankSelectEl.parentNode.querySelector('.nice-select .current');
    if (ns) v = (ns.textContent || ns.dataset?.value || '').trim();
  }
  return v;
}

/**
 * Setup Israeli bank selector: populate banks, wire bank/branch handlers.
 * @param {Element} container - Form container
 * @param {Object} formConfig - Form configuration
 * @param {{ url?: string, onNiceSelectUpdate?: (sel: HTMLSelectElement) => void }} options
 */
export function setup(container, formConfig, options = {}) {
  if (!container || !formConfig) return;
  const groups = buildBankGroupsFromConfig(formConfig);
  if (groups.length === 0) return;

  const loadOptions = options.url ? { url: options.url } : {};
  const onNiceSelectUpdate = options.onNiceSelectUpdate || (() => {});

  loadBranchMap(loadOptions).catch(() => {});

  const rowGroups = getAllRowGroups(container, groups);
  const selectorExcludes = new Map();
  rowGroups.forEach(rg => {
    if (!rg.group) return;
    const field = FormConfig.getFieldById(formConfig, rg.group.bankSelectorId);
    const codes = field && (field.excludeBankCodes || field.exclude_bank_codes);
    selectorExcludes.set(rg.group.bankSelectorId, Array.isArray(codes) ? codes : []);
  });
  const uniqueExcludeKeys = [...new Set([...selectorExcludes.values()].map(arr => JSON.stringify(arr)))];
  const namesByKey = {};
  Promise.all(uniqueExcludeKeys.map(key =>
    getBankNamesFromXml(loadOptions, { excludeBankCodes: JSON.parse(key) }).then(names => { namesByKey[key] = names; })
  )).then(() => {
    rowGroups.forEach(rowGroup => {
      const { bankSelect, branchInput, branchNumberInput, bankNumberInput, group } = rowGroup;
      if (!bankSelect || !group) return;
      const excludeArr = selectorExcludes.get(group.bankSelectorId) || [];
      const bankNames = namesByKey[JSON.stringify(excludeArr)] || [];
      const placeholder = bankSelect.querySelector('option[value=""]');
      while (bankSelect.options.length > 0) bankSelect.remove(0);
      if (placeholder) bankSelect.appendChild(placeholder);
      bankNames.forEach(name => {
        const opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name;
        bankSelect.appendChild(opt);
      });
      if (window.NiceSelect && bankSelect.classList?.contains('tqf_select')) {
        setTimeout(() => onNiceSelectUpdate(bankSelect), 50);
      }
    });

    const onBankChange = (rowGroup) => {
      const { bankSelect, branchInput, branchNumberInput, bankNumberInput, group } = rowGroup;
      const bankName = getBankSelectValue(bankSelect);
      if (!bankName) {
        [branchInput, branchNumberInput, bankNumberInput].filter(Boolean).forEach(el => {
          el.value = '';
          el._tqtagBranches = [];
          el.removeAttribute('list');
          if (el._tqtagDatalist) {
            el._tqtagDatalist.remove();
            el._tqtagDatalist = null;
          }
        });
        if (bankNumberInput) bankNumberInput.value = '';
        if (bankSelect) bankSelect._tqtagBranches = [];
        return;
      }
      getBankCodeForBankName(bankName, loadOptions).then(bankCode => {
        if (bankNumberInput) {
          bankNumberInput.value = bankCode || '';
          bankNumberInput.dispatchEvent(new Event('input', { bubbles: true }));
          bankNumberInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        let excludeBranchCodes = [];
        if (group) {
          const branchField = FormConfig.getFieldById(formConfig, group.branchId) || FormConfig.getFieldById(formConfig, group.branchNumberId);
          const excludeBranches = branchField && (branchField.excludeBranches || branchField.exclude_branches);
          if (Array.isArray(excludeBranches) && excludeBranches.length > 0) {
            const entry = excludeBranches.find(e =>
              String(e.bankCode || e.bank_code || '').trim() === String(bankCode || '').trim()
            );
            excludeBranchCodes = entry && Array.isArray(entry.branchCodes || entry.branch_codes)
              ? (entry.branchCodes || entry.branch_codes)
              : [];
          }
        }
        getBranchesForBank(bankName, loadOptions, { excludeBranchCodes }).then(branches => applyBranchesToRow(rowGroup, branches));
      });
    };

    function applyBranchesToRow(rowGroup, branches) {
      const { bankSelect, branchInput, branchNumberInput } = rowGroup;
      const branchData = branches.map(({ branchCode, branchName }) => ({ branchCode, branchName }));
      if (bankSelect) bankSelect._tqtagBranches = branchData;
      if (branchInput) {
        branchInput._tqtagBranches = branchData;
        if (branchInput._tqtagDatalist) branchInput._tqtagDatalist.remove();
        const dl = document.createElement('datalist');
        dl.id = `tqtag-dl-branch-${branchInput.name || branchInput.id || 'x'}-${Math.random().toString(36).slice(2, 9)}`;
        branches.forEach(({ branchCode, branchName }) => {
          const opt = document.createElement('option');
          opt.value = branchName || branchCode;
          opt.textContent = (branchCode || '') + ' - ' + (branchName || branchCode || '');
          dl.appendChild(opt);
        });
        document.body.appendChild(dl);
        branchInput._tqtagDatalist = dl;
        branchInput.setAttribute('list', dl.id);
      }
    }

    rowGroups.forEach(rowGroup => {
      const { bankSelect } = rowGroup;
      if (!bankSelect) return;
      bankSelect.addEventListener('change', () => onBankChange(rowGroup));
      const wrap = bankSelect.parentNode?.querySelector('.nice-select');
      if (wrap) {
        wrap.addEventListener('click', (e) => {
          if (e.target.closest?.('.option')) setTimeout(() => onBankChange(rowGroup), 150);
        });
      }
      onBankChange(rowGroup);
    });

    const handleBranchInput = (el) => {
      const rowGroup = getBankGroupForElement(el, groups);
      const branches = rowGroup?.bankSelect?._tqtagBranches;
      if (!rowGroup || !branches?.length) return;
      const val = (el.value || '').trim();
      if (!val) return;
      const match = branches.find(b => {
        const bc = String(b.branchCode || '').trim();
        const bn = String(b.branchName || b.branchCode || '').trim();
        return bc === val || bn === val;
      });
      if (!match) return;
      const branchNumberInput = rowGroup.branchNumberInput;
      if (branchNumberInput && String(branchNumberInput.value || '').trim() !== String(match.branchCode).trim()) {
        branchNumberInput.value = match.branchCode;
        branchNumberInput.dispatchEvent(new Event('input', { bubbles: true }));
        branchNumberInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const storeBranchesOnRowGroups = () => {
      rowGroups.forEach(rg => {
        const bankName = getBankSelectValue(rg.bankSelect);
        if (!bankName || rg.bankSelect?._tqtagBranches?.length) return;
        const getExcludeBranchCodes = (bankCode) => {
          if (!rg.group) return [];
          const branchField = FormConfig.getFieldById(formConfig, rg.group.branchId) || FormConfig.getFieldById(formConfig, rg.group.branchNumberId);
          const excludeBranches = branchField && (branchField.excludeBranches || branchField.exclude_branches);
          if (!Array.isArray(excludeBranches) || excludeBranches.length === 0) return [];
          const entry = excludeBranches.find(e =>
            String(e.bankCode || e.bank_code || '').trim() === String(bankCode || '').trim()
          );
          return entry && Array.isArray(entry.branchCodes || entry.branch_codes) ? (entry.branchCodes || entry.branch_codes) : [];
        };
        getBankCodeForBankName(bankName, loadOptions).then(bankCode => {
          const excludeBranchCodes = getExcludeBranchCodes(bankCode);
          getBranchesForBank(bankName, loadOptions, { excludeBranchCodes }).then(branches => {
            const branchData = branches.map(({ branchCode, branchName }) => ({ branchCode, branchName }));
            if (rg.bankSelect) rg.bankSelect._tqtagBranches = branchData;
            if (rg.branchInput) rg.branchInput._tqtagBranches = branchData;
          });
        });
      });
    };

    container.addEventListener('input', (e) => {
      const el = e.target;
      const fn = el?.getAttribute?.('data-input-function');
      if (fn !== INPUT_FN.BRANCH && fn !== INPUT_FN.BRANCH_NUMBER) return;
      const rowGroup = getBankGroupForElement(el, groups);
      if (rowGroup) {
        if (rowGroup.bankSelect?._tqtagBranches?.length) {
          setTimeout(() => handleBranchInput(el), 0);
        } else {
          storeBranchesOnRowGroups();
          setTimeout(() => handleBranchInput(el), 100);
        }
      }
    }, true);
    container.addEventListener('change', (e) => {
      const el = e.target;
      const fn = el?.getAttribute?.('data-input-function');
      if (fn !== INPUT_FN.BRANCH && fn !== INPUT_FN.BRANCH_NUMBER) return;
      const rowGroup = getBankGroupForElement(el, groups);
      if (rowGroup?.bankSelect?._tqtagBranches?.length) setTimeout(() => handleBranchInput(el), 0);
    }, true);
    container.addEventListener('blur', (e) => {
      const el = e.target;
      if (el?.getAttribute?.('data-input-function') !== INPUT_FN.BRANCH && el?.getAttribute?.('data-input-function') !== INPUT_FN.BRANCH_NUMBER) return;
      const rowGroup = getBankGroupForElement(el, groups);
      if (rowGroup?.bankSelect?._tqtagBranches?.length) setTimeout(() => handleBranchInput(el), 50);
    }, true);
    container.addEventListener('keyup', (e) => {
      if (e.key !== 'Enter') return;
      const el = e.target;
      const fn = el?.getAttribute?.('data-input-function');
      if (fn !== INPUT_FN.BRANCH && fn !== INPUT_FN.BRANCH_NUMBER) return;
      const rowGroup = getBankGroupForElement(el, groups);
      if (rowGroup?.bankSelect?._tqtagBranches?.length) setTimeout(() => handleBranchInput(el), 0);
    }, true);

    container.addEventListener('focusin', (e) => {
      const el = e.target;
      const fn = el?.getAttribute?.('data-input-function');
      if (fn !== INPUT_FN.BRANCH && fn !== INPUT_FN.BRANCH_NUMBER) return;
      const rowGroup = getBankGroupForElement(el, groups);
      if (!rowGroup || rowGroup.bankSelect?._tqtagBranches?.length > 0) return;
      const bankName = getBankSelectValue(rowGroup.bankSelect);
      if (!bankName) return;
      let excludeBranchCodes = [];
      if (rowGroup.group) {
        const branchField = FormConfig.getFieldById(formConfig, rowGroup.group.branchId) || FormConfig.getFieldById(formConfig, rowGroup.group.branchNumberId);
        const excludeBranches = branchField && (branchField.excludeBranches || branchField.exclude_branches);
        if (Array.isArray(excludeBranches) && excludeBranches.length > 0) {
          getBankCodeForBankName(bankName, loadOptions).then(bankCode => {
            const entry = excludeBranches.find(x =>
              String(x.bankCode || x.bank_code || '').trim() === String(bankCode || '').trim()
            );
            excludeBranchCodes = entry && Array.isArray(entry.branchCodes || entry.branch_codes) ? (entry.branchCodes || entry.branch_codes) : [];
            getBranchesForBank(bankName, loadOptions, { excludeBranchCodes }).then(branches => applyFocusinBranches(rowGroup, el, fn, branches));
          });
          return;
        }
      }
      getBranchesForBank(bankName, loadOptions, { excludeBranchCodes }).then(branches => applyFocusinBranches(rowGroup, el, fn, branches));
    }, true);

    function applyFocusinBranches(rowGroup, el, fn, branches) {
      if (rowGroup.bankSelect?._tqtagBranches?.length > 0) return;
      const branchData = branches.map(({ branchCode, branchName }) => ({ branchCode, branchName }));
      if (rowGroup.bankSelect) rowGroup.bankSelect._tqtagBranches = branchData;
      if (el._tqtagBranches) return;
      el._tqtagBranches = branchData;
      const isNumber = fn === INPUT_FN.BRANCH_NUMBER;
      const dl = document.createElement('datalist');
      dl.id = `tqtag-dl-focus-${el.name || el.id}-${isNumber ? 'n' : 't'}-${Math.random().toString(36).slice(2, 9)}`;
      branches.forEach(({ branchCode, branchName }) => {
        const opt = document.createElement('option');
        opt.value = isNumber ? branchCode : (branchName || branchCode);
        opt.textContent = (branchCode || '') + ' - ' + (branchName || branchCode || '');
        dl.appendChild(opt);
      });
      document.body.appendChild(dl);
      if (el._tqtagDatalist) el._tqtagDatalist.remove();
      el._tqtagDatalist = dl;
      el.setAttribute('list', dl.id);
    }
  }).catch(() => {
    const rowGroups = getAllRowGroups(container, groups);
    rowGroups.forEach(rg => {
      if (rg.bankSelect) {
        const bankName = getBankSelectValue(rg.bankSelect);
        if (bankName) getBranchesForBank(bankName, loadOptions).then(() => {});
      }
    });
  });
}
